import { useCallback, useMemo, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  DataTable,
  EmptyState,
  EmptyStateTitle,
  Link,
  Panel,
  PanelActions,
  PanelDescription,
  PanelHeader,
  PanelInset,
  PanelTitle,
  Paragraph,
} from '@customerio/pluma-components/react';
import {
  ExpandedRow,
  createRecentActivityColumns,
  specificActivityViewedProps,
  useObjectTypeById,
  type ActivityLogRow,
} from '@/features/activity-feed';
import { useLogs } from '@/services/db/activity-log/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import type { ExpandedState } from '@tanstack/react-table';
import { useAnalytics } from '@/services/analytics';
import { getActivityLogType } from '../utils/item';
import type { DataIndexItemAddress } from '../urls';

/**
 * The last few things that happened to this item.
 *
 * Five rows, because the point is "is this still arriving?" rather than a
 * feed — the full log is one click away, already narrowed to this item. The
 * first row opens on arrival, so the most recent payload is readable without
 * a click.
 */

/** How many rows the panel shows before sending you to the full log. */
const RECENT_LIMIT = 5;

function NoRecentActivity() {
  return (
    <EmptyState>
      <EmptyStateTitle icon="activity">No recent activity</EmptyStateTitle>
    </EmptyState>
  );
}

function RecentActivityError() {
  return <Paragraph variant="secondary">Could not load recent activity</Paragraph>;
}

/**
 * The table shows one slot when it has no rows, and a failed read and an
 * item nothing has happened to are both no rows. Which of the two it was is
 * the only thing that tells the reader whether to try again.
 */
function emptyStateForFetch(isError: boolean) {
  return isError ? RecentActivityError : NoRecentActivity;
}

type RecentActivityProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
  /**
   * Whether the newest log opens on arrival. The item's own page does; the
   * drawer does not, because a payload is a lot to put in a side panel — the
   * page this replaces makes the same distinction.
   */
  withNewestExpanded?: boolean;
};

/**
 * What the panel reads and records. Kept out of the markup: the reads, the
 * columns, the row's own event and the link to the whole feed are four
 * different questions, and none of them is about what the panel looks like.
 */
/** The log type that names this item, which is how its own activity is found. */
function itemLogType(address: DataIndexItemAddress): string {
  return getActivityLogType(
    address.scope.kind === 'profile'
      ? { id: address.name, kind: address.kind === 'event' ? 'event' : 'attribute' }
      : {
          id: address.name,
          kind: address.scope.kind === 'relationship' ? 'object-relationship-attribute' : 'object-attribute',
          object_type_id: address.scope.objectTypeId,
        },
  );
}

/**
 * Expanding a row is the row's own event, and the page this replaces fires it
 * wherever the row is shown rather than on the feed alone.
 *
 * Only a row the reader newly opens counts. Collapsing turns up nothing, and
 * the row the panel opens by itself was never a choice anyone made — so it
 * counts as seen from the start, until the reader touches it themselves.
 */
function useExpansionTracking(rows: readonly ActivityLogRow[], seeded: Record<string, boolean>) {
  const { track } = useAnalytics();
  const expandedRef = useRef<Record<string, boolean>>({});

  return useCallback(
    (next: ExpandedState) => {
      const expanded = next === true ? {} : next;
      const seen = { ...seeded, ...expandedRef.current };
      const newlyExpanded = Object.keys(expanded).filter((id) => expanded[id] === true && seen[id] !== true);
      expandedRef.current = expanded;

      for (const id of newlyExpanded) {
        const row = rows.find((candidate) => candidate.log.id === id);
        if (row != null) {
          track('logs', 'specificActivityViewed', specificActivityViewedProps('identified', row.log));
        }
      }
    },
    [rows, seeded, track],
  );
}

/**
 * The whole feed, narrowed to this item.
 *
 * The path comes from the router; the query is appended by hand. An item's
 * name is customer-authored, and `buildLocation` writes search values through
 * `JSON.stringify` — a name like `2024` would arrive at the log as the
 * three-character string `"2024"`, which matches nothing.
 */
function useFullLogHref(workspaceId: number, type: string, name: string): string {
  const router = useRouter();

  const path = router.buildLocation({
    to: '/workspaces/$workspaceId/profile_activity/$tab',
    params: { workspaceId: String(workspaceId), tab: 'identified' },
  }).href;

  return `${path}?${new URLSearchParams({ type, name }).toString()}`;
}

/** The row this panel renders under each log, for every log it shows. */
function useExpandedRowComponent({ workspaceId, address }: RecentActivityProps, logs: ReturnType<typeof useLogs>) {
  const environment = useEnvironmentOptional();
  const campaignSingular = 'Automation';
  const environmentIdentifiers = environment.data?.environment?.identifiers;
  const identifiers = useMemo(() => environmentIdentifiers ?? {}, [environmentIdentifiers]);
  const { segmentById, refetch } = logs;

  return useCallback(
    ({ row }: { row: { original: ActivityLogRow } }) => (
      <ExpandedRow
        row={row.original}
        workspaceId={String(workspaceId)}
        // Already resolved here: the item page reads it through
        // requireWorkspaceId rather than off the route, so there is no alias
        // to tell apart.
        dataWorkspaceId={String(workspaceId)}
        campaignSingular={campaignSingular}
        isAnonymous={false}
        identifiers={identifiers}
        segmentById={segmentById}
        onResent={() => void refetch()}
        // The funnels count this surface apart from the whole feed, and a log
        // with no name of its own opens on the item's.
        resend={{ source: 'Data Index Event Recent Activity', eventName: address.name }}
      />
    ),
    [workspaceId, address.name, campaignSingular, identifiers, segmentById, refetch],
  );
}

/**
 * What the panel reads and records, composed from the four questions it asks:
 * which logs are this item's, what the rows look like, what expanding one
 * reports, and where the whole feed is.
 */
function useItemActivityFeed({ workspaceId, address, withNewestExpanded = true }: RecentActivityProps) {
  const objectTypeById = useObjectTypeById(String(workspaceId));
  const type = itemLogType(address);

  const logs = useLogs({
    environmentId: String(workspaceId),
    tab: 'identified',
    filters: { type, name: address.name, from: '', to: '' },
    limit: RECENT_LIMIT,
  });

  // The most recent payload is readable without a click. The table reads the
  // seed once, on mount, so the key remounts it when the newest log changes —
  // which is also what drops a stale expansion after a resend refetches.
  const newestLogId = logs.rows[0]?.log.id;
  const seeded = useMemo(
    () => (newestLogId == null || !withNewestExpanded ? {} : { [newestLogId]: true }),
    [newestLogId, withNewestExpanded],
  );

  const personSingular = 'Person';
  const isObject = address.scope.kind === 'object';
  const columns = useMemo(
    () => createRecentActivityColumns({ workspaceId: String(workspaceId), personSingular, isObject, objectTypeById }),
    [isObject, personSingular, workspaceId, objectTypeById],
  );

  return {
    logs,
    columns,
    seeded,
    newestLogId,
    trackRowsExpanded: useExpansionTracking(logs.rows, seeded),
    expandedRowComponent: useExpandedRowComponent({ workspaceId, address }, logs),
    fullLogHref: useFullLogHref(workspaceId, type, address.name),
    feedTerm: 'activity',
  };
}

export function RecentActivity({ workspaceId, address, withNewestExpanded = true }: RecentActivityProps) {
  const { logs, columns, seeded, newestLogId, trackRowsExpanded, expandedRowComponent, fullLogHref, feedTerm } =
    useItemActivityFeed({ workspaceId, address, withNewestExpanded });

  return (
    <Panel>
      <PanelHeader withBorder>
        <PanelTitle>Recent Activity</PanelTitle>
        <PanelDescription>Reflects only the last 30 days of activity</PanelDescription>
        <PanelActions>
          <Link
            href={fullLogHref}
            icon="exit"
            iconSize="sm"
            iconPosition="trailing"
            isUnderlined={false}
            target="_blank"
          >
            See full {feedTerm}
          </Link>
        </PanelActions>
      </PanelHeader>
      <PanelInset>
        <DataTable
          key={newestLogId}
          // Named for the kind of item, not the item, and ended with a colon,
          // as the page this replaces captions it
          // (`data-index-item/recent-activity/index.gts:126`).
          caption={`Activity for ${address.kind}:`}
          data={logs.rows}
          columns={columns}
          isLoading={logs.isLoading}
          emptyStateComponent={emptyStateForFetch(logs.isError)}
          getRowId={(row) => row.log.id}
          // No expander column: the chevron sits at the end of the timestamp
          // cell, as it does there, and the whole row toggles on a click.
          getRowCanExpand={() => true}
          expandedRowComponent={expandedRowComponent}
          onRowExpansionChange={trackRowsExpanded}
          initiallyExpandedRows={seeded}
        />
      </PanelInset>
    </Panel>
  );
}
